import { queryOptions, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { redirect, useParams } from '@tanstack/react-router'
import { api, unwrap } from '@/lib/api'
import type { components } from '@/api/schema'

export type Session = components['schemas']['Session']

/** The signed-in person, active organisation, permissions and workspaces. */
export const sessionQuery = queryOptions({
  queryKey: ['session'] as const,
  queryFn: () => unwrap(api.GET('/esm/v1/session')),
  staleTime: 30_000,
  retry: false,
})

/**
 * beforeLoad guard for staff-only (platform) routes. The nav already hides
 * them; this stops a non-staff person who opens one by URL from ever
 * rendering it — they land on their normal home instead.
 */
export async function requireSuperAdmin({ context }: { context: { queryClient: QueryClient } }): Promise<void> {
  const s = await context.queryClient.ensureQueryData(sessionQuery)
  if (!s.is_super_admin) throw redirect({ to: '/' })
}

export function useSession(): Session {
  const q = useQuery(sessionQuery)
  // _app's beforeLoad guarantees the session is loaded before any app screen renders.
  return q.data as Session
}

export function useSessionMaybe(): Session | undefined {
  return useQuery(sessionQuery).data
}

export function useInvalidateSession(): () => Promise<void> {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ['session'] })
}

/** Does the person hold ANY of these permissions in the active organisation? */
export function useCan(perm: string | string[] | undefined): boolean {
  const s = useSessionMaybe()
  if (!perm) return true
  if (!s) return false
  const need = Array.isArray(perm) ? perm : [perm]
  return need.some(p => (s.permissions as string[]).includes(p))
}

const LAST_WS = 'esm-last-workspace'

export function rememberWorkspace(id: string): void {
  try {
    localStorage.setItem(LAST_WS, id)
  } catch {
    /* ignore */
  }
}

/** The workspace in the URL, else the last one used, else the first one. */
export function useWorkspaceId(): string | null {
  const params = useParams({ strict: false }) as { ws?: string }
  const s = useSessionMaybe()
  if (params.ws) return params.ws
  let last: string | null = null
  try {
    last = localStorage.getItem(LAST_WS)
  } catch {
    last = null
  }
  const list = s?.workspaces ?? []
  if (last && list.some(w => w.id === last)) return last
  return list[0]?.id ?? null
}

export function useWorkspace() {
  const id = useWorkspaceId()
  const s = useSessionMaybe()
  return s?.workspaces.find(w => w.id === id) ?? null
}
